// Sprint fleet-resource-metrics (spec §2 D10, §3) — per-row mini-history
// chart for CPU/RAM/disk. New (verified no equivalent exists — grepped
// `web/src/components/ui` before writing this; no sparkline/chart
// primitive present). Small, dependency-free inline SVG — matches the
// existing `<Icon>` pattern of numeric geometry props (not a
// design-discipline violation; SVG width/height/strokeWidth are
// functional geometry, not visual-token drift, same class as `size={14}`
// on `<Icon>` in `RunnersTable.tsx`).
//
// Renders three overlaid polylines (cpu/ram/disk), each colored with an
// existing semantic token via `currentColor` (no raw hex). Values are
// clamped to [0, 100] at render (D1 — cpuPct has no upper bound on the
// wire; a loaded box can read >100%). A gap in the data (a sample with
// `null` for one series) breaks that series' line at that point rather
// than interpolating across missing data.

import { useMemo } from 'react';
import { useTranslation } from '../../i18n';
import type { RunnerMetricsSample } from '../../lib/api';

export interface RunnerMetricsSparklineProps {
  samples: RunnerMetricsSample[];
  width?: number;
  height?: number;
  className?: string;
}

type SeriesKey = 'cpuPct' | 'ramPctUsed' | 'diskPctUsed';

const SERIES: Array<{ key: SeriesKey; colorClass: string; label: string }> = [
  { key: 'cpuPct', colorClass: 'text-status-info', label: 'CPU' },
  { key: 'ramPctUsed', colorClass: 'text-status-warning', label: 'RAM' },
  { key: 'diskPctUsed', colorClass: 'text-status-success', label: 'Disk' },
];

const DEFAULT_WIDTH = 64;
const DEFAULT_HEIGHT = 20;

// Builds one <polyline> `points` string per contiguous run of non-null
// samples (a gap in the middle of the series produces two separate
// segments rather than a straight line jumping across the hole).
function buildSegments(values: Array<number | null>, width: number, height: number): string[] {
  const n = values.length;
  if (n === 0) return [];
  const segments: string[] = [];
  let current: string[] = [];
  values.forEach((raw, i) => {
    if (raw === null) {
      if (current.length >= 2) segments.push(current.join(' '));
      current = [];
      return;
    }
    const clamped = Math.min(100, Math.max(0, raw));
    const x = n <= 1 ? 0 : (i / (n - 1)) * width;
    const y = height - (clamped / 100) * height;
    current.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  });
  if (current.length >= 2) segments.push(current.join(' '));
  return segments;
}

export function RunnerMetricsSparkline({
  samples,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  className,
}: RunnerMetricsSparklineProps) {
  const { t } = useTranslation();
  const lines = useMemo(
    () =>
      SERIES.flatMap((s) =>
        buildSegments(
          samples.map((sample) => sample[s.key]),
          width,
          height,
        ).map((points, idx) => ({ ...s, points, segmentKey: `${s.key}-${idx}` })),
      ),
    [samples, width, height],
  );

  if (lines.length === 0) {
    return (
      <span className="text-label text-muted-foreground" data-testid="runner-metrics-sparkline-empty">
        {t('settings.runners.trendEmpty')}
      </span>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      role="img"
      aria-label={t('settings.runners.trendAriaLabel')}
      data-testid="runner-metrics-sparkline"
    >
      {lines.map((l) => (
        <polyline
          key={l.segmentKey}
          points={l.points}
          fill="none"
          className={l.colorClass}
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
